if (!customElements.get('product-modal')) {
  customElements.define(
    'product-modal',
    class ProductModal extends ModalDialog {
      /* Lunai R2.2: the modal is the PDP lightbox — arrow/keyboard
         navigation over all media, a live counter, current-variant line
         and an add-to-cart proxy. ModalDialog supplies ESC, scrim close,
         focus trap and focus-return-to-opener. */
      constructor() {
        super();
        this.prevBtn = this.querySelector('[data-lb-prev]');
        this.nextBtn = this.querySelector('[data-lb-next]');
        this.counter = this.querySelector('[data-lb-count]');
        this.atc = this.querySelector('[data-lb-atc]');
        this.variantLine = this.querySelector('[data-lb-variant]');
        if (this.prevBtn) this.prevBtn.addEventListener('click', () => this.step(-1));
        if (this.nextBtn) this.nextBtn.addEventListener('click', () => this.step(1));
        /* Prompt 6: Dawn's ModalDialog closes any `.media-modal` on a mouse
           `pointerup` that did not land inside a deferred-media/product-model
           (global.js) — its "click the photo to dismiss" gesture. The Lunai
           lightbox puts its own chrome INSIDE that dialog, so every mouse click
           on an arrow advanced the photo AND dismissed the lightbox in the same
           gesture: prev/next were keyboard-only in practice, and clicking the
           title, price or variant line closed it too. Stop the gesture at the
           controls; the image area keeps click-to-dismiss. */
        this.querySelectorAll('.pd-lb__arrow, .pd-lb__count, .pd-lb__info').forEach((el) => {
          el.addEventListener('pointerup', (event) => event.stopPropagation());
        });
        /* document-level so arrows work regardless of which element
           inside the trap currently holds focus */
        document.addEventListener('keydown', (event) => {
          if (!this.hasAttribute('open')) return;
          if (event.code === 'ArrowLeft') {
            event.preventDefault();
            this.step(-1);
          }
          if (event.code === 'ArrowRight') {
            event.preventDefault();
            this.step(1);
          }
        });
        if (this.atc) {
          this.atc.addEventListener('click', () => {
            const real = document.querySelector('.product-form__submit');
            this.hide();
            if (real && !real.disabled) real.click();
          });
        }
      }

      hide() {
        super.hide();
      }

      show(opener) {
        super.show(opener);
        this.showActiveMedia();
        this.syncVariantLine();
        this.syncPrice();
      }

      /* Prompt 6: the info panel is Liquid-rendered once, and Dawn's variant
         fetch only replaces `.product-media-modal__content` — the panel beside
         it was never touched. So on the four products whose variants differ in
         price, picking the cheaper one and then opening the lightbox showed the
         FIRST variant's price next to the correct variant name (30 ml priced at
         €18,50 instead of €7,20 on `suhi-sampon-in-balzam`). Mirror whatever
         the purchase column is currently showing — Dawn keeps that block in
         step through `updateSourceFromDestination('price')`. */
      syncPrice() {
        const host = this.querySelector('.pd-lb__price');
        if (!host) return;
        const live = document.getElementById(`price-${this.dataset.section}`);
        if (live && live.innerHTML.trim()) host.innerHTML = live.innerHTML;
      }

      /* Phase 4 (§20): the lightbox navigates the SELECTED COLOUR's media
         only. Off-colour items are marked `lunai-media-hidden` by Liquid and
         kept in sync by lunai-product.js; Dawn's own `--variant` exclusivity
         is CSS-only and breakpoint-dependent, so we also drop anything the
         browser is not actually displaying. Without this, picking gold and
         pressing "next" walked into the white and blue photos and the counter
         read "1 / 20" for a five-photo colourway. */
      mediaList() {
        /* Both exclusions are decided in Liquid, so this holds at every
           breakpoint. We deliberately do NOT test offsetParent/display:
           below 750px the modal shows one medium at a time
           (`:not(.active)` is display:none) and everything except the
           current slide would look hidden. */
        return Array.from(this.querySelectorAll('[role="document"] [data-media-id]')).filter(
          (el) =>
            !el.classList.contains('lunai-media-hidden') && !el.classList.contains('lunai-media-off-variant')
        );
      }

      currentIndex() {
        const list = this.mediaList();
        const active = this.querySelector('[data-media-id].active');
        const i = list.indexOf(active);
        return i >= 0 ? i : 0;
      }

      step(delta) {
        const list = this.mediaList();
        if (!list.length) return;
        let i = this.currentIndex() + delta;
        if (i < 0) i = list.length - 1;
        if (i >= list.length) i = 0;
        this.activate(list[i]);
      }

      activate(media) {
        this.mediaList().forEach((el) => el.classList.toggle('active', el === media));
        const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        media.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'center' });
        this.updateCounter();
      }

      updateCounter() {
        if (!this.counter) return;
        const list = this.mediaList();
        if (list.length < 2) {
          this.counter.hidden = true;
          if (this.prevBtn) this.prevBtn.hidden = true;
          if (this.nextBtn) this.nextBtn.hidden = true;
          return;
        }
        this.counter.hidden = false;
        this.counter.textContent = this.currentIndex() + 1 + ' / ' + list.length;
      }

      syncVariantLine() {
        if (!this.variantLine) return;
        const json = document.querySelector('variant-selects [data-selected-variant]');
        let v = null;
        try {
          v = json && JSON.parse(json.textContent);
        } catch (e) {
          v = null;
        }
        if (v && v.title && v.title !== 'Default Title') {
          this.variantLine.textContent = v.title.split(' / ').join(' · ');
          this.variantLine.hidden = false;
        } else {
          this.variantLine.hidden = true;
        }
      }

      showActiveMedia() {
        this.querySelectorAll(
          `[data-media-id]:not([data-media-id="${this.openedBy.getAttribute('data-media-id')}"])`
        ).forEach((element) => {
          element.classList.remove('active');
        });
        const activeMedia = this.querySelector(`[data-media-id="${this.openedBy.getAttribute('data-media-id')}"]`);
        if (!activeMedia) return;
        const activeMediaTemplate = activeMedia.querySelector('template');
        const activeMediaContent = activeMediaTemplate ? activeMediaTemplate.content : null;
        activeMedia.classList.add('active');
        activeMedia.scrollIntoView();

        const container = this.querySelector('[role="document"]');
        container.scrollLeft = (activeMedia.width - container.clientWidth) / 2;
        this.updateCounter();

        if (
          activeMedia.nodeName == 'DEFERRED-MEDIA' &&
          activeMediaContent &&
          activeMediaContent.querySelector('.js-youtube')
        )
          activeMedia.loadContent();
      }
    }
  );
}
